<template>
	<div v-if="slideId == '0' || !slideId" class="text-center">
		<v-container grid-list-md text-xs-center>
			<v-layout row wrap>
				<v-flex xs12>
					<div class="lp-tree-edit-no-slide-img-container">
						<img
							class="lp-tree-edit-no-slide-img"
							:src="require('../../../components/slides/placeholder-no-slide.svg')"
						/>
					</div>
				</v-flex>
				<v-flex xs12>
					<v-btn class="mt-4" outline color="default" @click="addCard">
						<v-icon left>mdi-shape-rectangle-plus</v-icon>
						Add
						<div v-if="isLayoutParent">&nbsp;Layout</div>
						<div v-else>&nbsp;Slide</div>
					</v-btn>
				</v-flex>
			</v-layout>
		</v-container>
	</div>
	<div
		v-else
		class="lp-slide-wrapper"
		:class="{ dark: slideStyleData.dark }"
		ref="viewer"
		tabindex="0"
		:style="settingStyles.wrapper"
		@click.self="cleanSelection"
		@drop="onBlockDrop"
		@dragover.prevent
		@dragenter.prevent
		@keyup.alt="handleShortcuts"
	>
		<div class="lp-slide" ref="canvas" :style="slideStyles">
			<block-canvas-grid
				@zoomfit="setZoomFit"
				v-if="slideStyleData.gridSize && slideStyleData.gridSize[0] > 1"
				:scale="slideStyleData.scale"
				:origin-x="slideStyleData.origin.x"
				:origin-y="slideStyleData.origin.y"
				:grid-x="slideStyleData.gridSize[0]"
				:grid-y="slideStyleData.gridSize[1]"
				:canvasWidth="slideStyleData.viewerWidth"
				:canvasHeight="slideStyleData.viewerHeight"
			/>

			<div :style="styleCanvas" @click.self="cleanSelection">
				<component
					v-for="block in blocks"
					:key="block.id"
					:is="getBlockComponent(block.type)"
					:value="block"
					:mode="mode ? mode : 'edit'"
					@blockClick="handleClick(block)"
					@blockDblClick="handleDoubleClick(block)"
					@create:draft="$emit('create:draft', $event)"
					:blockVariables="globalVariables"
				/>
			</div>

      <template v-if="hasOverlays">
				<block-overlay
          v-for="overlay in blocksSelector.overlaysQueue"
          :key="overlay.id"
          :overlayId="overlay.id"
					:defaultBlockStyles="defaultBlockStyles"
					:globalVariables="globalVariables"
					@create:draft="$emit('create:draft', $event)"
				/>
			</template>

			<subtitles-overlay />
		</div>
	</div>
</template>

<script>
import onBlockDrop from '../../_mixins/onBlockDrop.vue';
import blockCanvasCommon from '../../_mixins/blockCanvasCommon.vue';
import BlockOverlay from './BlockOverlay.vue';
import { Layout, Slide } from '../../../models/models';
import { PtModal } from 'plant-common';
import _ from 'lodash';
import SubtitlesOverlay from './SubtitlesOverlay.vue';
import BlockCanvasGrid from './BlockCanvasGrid.vue';
import { BlockSelectionState } from '../../_models/blockSelectionState';

export default {
	name: 'BlockCanvas',
	inject: ['blocksSelector', 'blockDefinitions', 'lessonPlanController', 'blockRemover', 'slideStyleData'],
	mixins: [onBlockDrop, blockCanvasCommon, PtModal],
	components: { BlockCanvasGrid, BlockOverlay, SubtitlesOverlay },
	props: {
		blocks: Array,
		defaultBlockStyles: Object,
		settingStyles: Object,
		globalVariables: Array,
		slideId: String,
		mode: String
	},
	data() {
		return {
			selectedBlock: null,
			selectedBlockState: null
		};
	},
	mounted() {
		if (this.$refs.viewer) {
			this.$refs.viewer.addEventListener('keydown', this.keyHandler);
		}
	},
	created() {
		if (!_.isEmpty(this.blocksSelector)) {
			this.blocksSelector.$on(`selection-changed`, ({ block, state }) => {
				this.selectedBlock = block;
				this.selectedBlockState = state;
			});
		}
	},
	unmounted() {
		// remove global events
		if (this.$refs.viewer) {
			this.$refs.viewer.removeEventListener('keydown', this.keyHandler);
		}
	},
	computed: {
		isLayoutParent() {
			if (this.$parent && this.$parent.$options) {
				return this.$parent.$options._componentTag == 'layout-designer';
			} else {
				return false;
			}
		},
    hasOverlays() {
      return this.blocksSelector.overlaysQueue && this.blocksSelector.overlaysQueue.length;
    }
	},
	methods: {
		setZoomFit() {
			this.$nextTick(() => {
				this.zoomFit();
			});
		},
		toggleGrid() {
			if (this.lessonPlanController.gridSize[0] !== 1) {
				this.lessonPlanController.prevGridSize = [...this.lessonPlanController.gridSize];
				this.lessonPlanController.gridSize = [1, 1];
			} else {
				this.lessonPlanController.gridSize = this.lessonPlanController.prevGridSize || [80, 45];
			}
		},
		setNextGrid(direction) {
			if (this.lessonPlanController.gridIndex === 0) {
				return;
			}

			const directionMap = {
				up: 1,
				down: -1
			};

			const nextGridIndex = this.lessonPlanController.gridIndex + directionMap[direction];

			if (nextGridIndex === this.lessonPlanController.gridSizes.length) {
				return;
			}

			this.lessonPlanController.gridSize = this.lessonPlanController.gridSizes[nextGridIndex];
		},
		handleShortcuts(event) {
			if (event.altKey && event.code === 'KeyG') {
				this.toggleGrid();
			}

			if (event.altKey && event.code === 'ArrowUp') {
				this.setNextGrid('up');
			}

			if (event.altKey && event.code === 'ArrowDown') {
				this.setNextGrid('down');
			}

			if (event.altKey && event.code === 'KeyP') {
				this.openPopupEdition();
			}
		},
		keyHandler(evt) {
			var { code } = evt;
			switch (code) {
				case 'Delete':
					if (this.selectedBlock && this.selectedBlockState != BlockSelectionState.editContent) {
						if (this.selectedBlock.protection && this.selectedBlock.protection.delete) {
							this.$modal('info', {
								title: 'Can not delete current block',
								message: `Selected block "${
									this.selectedBlock && this.selectedBlock.name ? this.selectedBlock.name : 'Block'
								}" [${
									this.selectedBlock && this.selectedBlock.type ? this.selectedBlock.type : 'block'
								}] is protected against deletion`,
								ok: {
									text: 'Accept',
									icon: 'mdi-delete-off-outline'
								}
							});
						} else {
							this.$modal('confirm', {
								title: `Are you sure you want to delete selected block "${
									this.selectedBlock && this.selectedBlock.name ? this.selectedBlock.name : 'Block'
								}" [${this.selectedBlock && this.selectedBlock.type ? this.selectedBlock.type : 'block'}]?`,
								accept: {
									color: 'error',
									text: 'Delete',
									icon: 'mdi-delete'
								},
								cancel: { text: 'cancel', color: 'shades' }
							}).then(r => {
								if (!r) {
									this.$refs.viewer.focus();
									return;
								}
								const id = this.selectedBlock.id;
								this.blocksSelector.selectParentBlock(id);
								this.blockRemover.deleteBlock(id);
							});
						}
					}
					break;
			}
		},
		cleanSelection() {
			this.blocksSelector.closeAllOverlays();
			this.blocksSelector.selectionClear();
		},
		handleClick(block) {
			this.$refs.viewer.focus();
			this.blocksSelector.selectionClear();
			this.$nextTick(() => {
				this.blocksSelector.selectionAdd(block.id);
			});

			if (this.$el.querySelector('.movable:not(.disabled)')) {
				this.$el.querySelector('.movable').classList.remove('edit-disabled');
			}
		},
		handleDoubleClick(block) {
			const allowDClickBlocks = ['audio', 'video', 'libraryContent']; //allow double click to play/pause content in edit mode
			if (block.protection.data && !allowDClickBlocks.includes(block.type)) {
				this.$el.querySelector('.movable').classList.add('edit-disabled');
			} else {
				this.blocksSelector.selectionEdit(block.id);
			}
		},
		openPopupEdition() {
			if (!this.selectedBlock) {
				return;
			}

			if (this.selectedBlock.actions.length === 0) {
				return;
			}

			const action = this.selectedBlock.actions.find(action => action.trigger == 'click');
			if (!action) {
				return;
			}
			if (action.type !== 'popup') {
				return;
			}
			
			const overlay = action;
			overlay.parent = this.selectedBlock.id;

			this.blocksSelector.openNewOverlay(overlay);
		},
		addCard() {
			if (this.isLayoutParent) {
				const layout = new Layout();
				layout.slide = new Slide();
				this.lessonPlanController.lessonPlanContents.layouts.push(layout);
				this.lessonPlanController.selectLayoutById(layout.id);
			} else {
				this.lessonPlanController.addSlide();
			}
		}
	}
};
</script>

<style scoped>
.lp-tree-edit-no-slide-img-container {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 800px;
	padding-top: 80px;
}
</style>
